import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Ship } from '../../interfaces/types';
import { ShipList } from '../../components/ships/ShipList';
import { ShipCatalogView } from '../../components/ships/ShipCatalogView';
import { SearchBar } from '../../components/ships/SearchBar';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import { FiltersMenu, SortConfig } from '../../components/FiltersMenu/FiltersMenu';
import { TYPE_TRANSLATIONS } from '../../utils/translations';
import { authService } from '../../services/authService';
import { useApi } from '../../hooks/useApi';
import { Header } from '../../components/common/Header';
import { Navigation } from '../../components/common/Navigation';
import { Pagination } from '../../components/common/Pagination';
import { ViewModeButtons } from '../../components/common/ViewModeButtons';
import { ShipComparison } from '../../components/ships/ShipComparison';
import { ShipCard } from '../../components/ships/ShipCard';
import { EmptyFleetMessage } from '../../components/tutorial/EmptyFleetMessage';
import './FleetPage.css';
import '../../components/common/MenusLayout.css';
import { useUser } from '../../hooks/useUser';
import { useToast } from '../../components/common/ToastContainer';
import { fleetService } from '../../services/fleetService';
import {
  buildExportFilename,
  buildFleetViewerExport,
  buildStarjumpFleetExport,
  downloadJsonFile,
} from '../../utils/fleetExport';

interface ManufacturerResponse {
  code: string;
  name: string;
}

interface GroupedShip extends Ship {
  usuarios: string[];
  duplicateCount: number;
}

interface TutorialProps {
  isActive: boolean;
  isCompleted: boolean;
  hasNeverCompletedTutorial: () => boolean;
  startTutorial: () => void;
  triggerStepAction: (targetElement: string) => void;
}

interface FleetPageProps {
  tutorialProps?: TutorialProps;
}

interface ManufacturerGroup {
  ships: Ship[];
  name: string;
}

interface ShipsByManufacturer {
  [key: string]: ManufacturerGroup;
}

interface ShipsByType {
  [key: string]: Ship[];
}

interface ShipsBySize {
  [key: string]: Ship[];
}



const groupShips = (ships: Ship[], selectedUser?: string): GroupedShip[] => {
  const shipGroups = new Map<string, { ship: Ship; usuarios: Set<string>; count: number }>();
  
  ships.forEach(ship => {
    const key = ship.name;
    
    if (!shipGroups.has(key)) {
      shipGroups.set(key, {
        ship: { ...ship },
        usuarios: new Set(),
        count: 1
      });
    } else {
      const group = shipGroups.get(key)!;
      group.count++;
    }
    
    const group = shipGroups.get(key)!;
    if (ship.usuario) {
      group.usuarios.add(ship.usuario);
    }
  });

  return Array.from(shipGroups.values()).map(({ ship, usuarios, count }) => {
    // Si hay un filtro de usuario específico, el duplicateCount debe reflejar solo las naves de ese usuario
    const adjustedCount = selectedUser ? 
      ships.filter(s => s.name === ship.name && s.usuario === selectedUser).length : 
      count;
    
    return {
      ...ship,
      usuarios: Array.from(usuarios),
      duplicateCount: adjustedCount
    };
  });
};

export const FleetPage: React.FC<FleetPageProps> = ({ tutorialProps }) => {
  const [searchParams] = useSearchParams();
  const { data: rawShips, loading: shipsLoading, error: shipsError } = useApi<Ship[]>('/flota');
  const { data: catalogShips } = useApi<Ship[]>('/naves'); // Naves del catálogo para la vista
  const { data: manufacturers } = useApi<ManufacturerResponse[]>('/naves/marcas');
  const { data: shipTypes } = useApi<string[]>('/naves/tipos');
  const [, setSelectedShip] = useState<Ship | null>(null);
  const [currentManufacturer, setCurrentManufacturer] = useState<string>('all');
  const [showManufacturers, setShowManufacturers] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [showSizeMenu, setShowSizeMenu] = useState(false);
  const [showFocusMenu, setShowFocusMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [shipsToCompare, setShipsToCompare] = useState<Ship[]>([]);

  const [selectedType, setSelectedType] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedFocus, setSelectedFocus] = useState('');
  const [selectedProductionStatus, setSelectedProductionStatus] = useState('');
  const [showProductionStatusMenu, setShowProductionStatusMenu] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [isGrouped, setIsGrouped] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });
  const [viewType, setViewType] = useState<'grid' | 'list' | 'catalog'>('grid');
  const [catalogSortBy, setCatalogSortBy] = useState<'manufacturer' | 'type' | 'name'>('manufacturer');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const { 
    username, 
    isAdmin, 
    isCaptain
  } = useUser();
  
  const { showToast } = useToast();
  
  const [localUsername, setLocalUsername] = useState<string>(username || '');
  const [organizationUsers, setOrganizationUsers] = useState<string[]>([]);
  const hasInitializedUserFilter = useRef(false);
  
  // Estado y lógica del menú hamburguesa
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Cerrar menú al hacer clic fuera
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cerrar menú al cambiar de ruta
  React.useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  // Establecer "Mi Flota" por defecto cuando se carga el usuario (solo una vez, al inicio)
  React.useEffect(() => {
    if (localUsername && !hasInitializedUserFilter.current) {
      setSelectedUser(localUsername);
      hasInitializedUserFilter.current = true;
    }
  }, [localUsername]); // Solo ejecutar cuando cambia localUsername

  // Tutorial props
  const {
    isCompleted: isTutorialCompleted = false,
    hasNeverCompletedTutorial = () => false,
    startTutorial = () => {}
  } = tutorialProps || {};

  // Actualizar el username local cuando cambia el username del hook
  useEffect(() => {
    if (username) {
      setLocalUsername(username);
    }
  }, [username]);

  useEffect(() => {
    const userData = authService.getUserData();
    if (userData) {
      setIsAnonymous(false);
    }
  }, []);

  // Aplicar filtros desde la URL al cargar la página
  useEffect(() => {
    const type = searchParams.get('type');
    const size = searchParams.get('size');
    const manufacturer = searchParams.get('manufacturer');
    const user = searchParams.get('user');

    if (type) setSelectedType(type);
    if (size) setSelectedSize(size);
    if (manufacturer) setCurrentManufacturer(manufacturer);
    if (user) setSelectedUser(user === 'anonymous' ? '' : user);
  }, [searchParams]);

  const handleManufacturersToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
        setShowManufacturers(!showManufacturers);
  };

  const handleFiltersToggle = () => {
    setShowFilters(!showFilters);
  };

  const handleCompareToggle = () => {
    setIsCompareMode(!isCompareMode);
    if (isCompareMode) {
      setShipsToCompare([]);
    }
  };

  const handleShipCompareSelect = (ship: Ship) => {
    if (shipsToCompare.find(s => s.name === ship.name)) {
      setShipsToCompare(shipsToCompare.filter(s => s.name !== ship.name));
    } else if (shipsToCompare.length < 4) {
      setShipsToCompare([...shipsToCompare, ship]);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedType('');
    setSelectedSize('');
    setSelectedFocus('');
    setSelectedUser('');
        setCurrentManufacturer('all');
  };

  const toggleTypeMenu = () => setShowTypeMenu(!showTypeMenu);
  const toggleSizeMenu = () => setShowSizeMenu(!showSizeMenu);
  const toggleFocusMenu = () => setShowFocusMenu(!showFocusMenu);
  const toggleProductionStatusMenu = () => setShowProductionStatusMenu(!showProductionStatusMenu);
  const toggleUserMenu = () => setShowUserMenu(!showUserMenu);

  const exportFilenameParts = {
    manufacturer: currentManufacturer,
    type: selectedType,
    size: selectedSize,
    user: selectedUser,
  };

  const exportToFleetViewer = () => {
    try {
      const data = buildFleetViewerExport(filteredShips);
      downloadJsonFile(data, buildExportFilename('fleetviewer_export', exportFilenameParts));
      showToast(`Exportadas ${filteredShips.length} naves (FleetViewer)`, 'success');
    } catch (error) {
      showToast('Error al exportar a FleetViewer', 'error');
      console.error('Error en la exportación FleetViewer:', error);
    }
  };

  const exportToStarjump = () => {
    try {
      const data = buildStarjumpFleetExport(filteredShips);
      const shipCount = data.canvasItems.filter((item) => item.itemType === 'SHIP').length;
      downloadJsonFile(data, buildExportFilename('STARJUMP_FleetViewer', exportFilenameParts));
      showToast(`Exportadas ${shipCount} naves (Starjump FleetViewer)`, 'success');
    } catch (error) {
      showToast('Error al exportar a Starjump', 'error');
      console.error('Error en la exportación Starjump:', error);
    }
  };

  // Filtrar las naves según los criterios seleccionados
  const filteredShips = useMemo(() => {
    if (!rawShips) return [];
    
    // Mostrar todas las naves, sin filtrar por usuario
    return rawShips.filter((ship: Ship) => {
      const matchesSearch = !searchQuery || 
        ship.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = !selectedType || 
        (ship.type && ship.type.toLowerCase() === selectedType.toLowerCase());
      const matchesSize = !selectedSize || 
        (ship.size && ship.size.toLowerCase() === selectedSize.toLowerCase());
      const matchesFocus = !selectedFocus || ship.focus === selectedFocus;
      const matchesProductionStatus = !selectedProductionStatus || 
        (ship.production_status && ship.production_status.toLowerCase() === selectedProductionStatus.toLowerCase());
      const matchesUser = !selectedUser || ship.usuario === selectedUser;
      const matchesManufacturer = currentManufacturer === 'all' || 
        ship.manufacturer.code === currentManufacturer;

      return matchesSearch && matchesType && matchesSize &&
        matchesFocus && matchesProductionStatus && matchesUser && matchesManufacturer;
    });
  }, [rawShips, searchQuery, selectedType, selectedSize, selectedFocus, 
      selectedProductionStatus, selectedUser, currentManufacturer]);

  // Calcular conteos de naves en la flota (separado por usuario actual y otros usuarios)
  // Si selectedUser está activo (Mi flota), solo cuenta las naves del usuario seleccionado
  // Si selectedUser está vacío (Toda la flota), separa por usuario actual vs otros
  const fleetCounts = useMemo(() => {
    if (!rawShips) return { user: {}, others: {} };
    const userCounts: Record<string, number> = {};
    const otherCounts: Record<string, number> = {};
    
    // Si hay un usuario seleccionado (Mi flota), solo contar las naves de ese usuario
    if (selectedUser) {
      rawShips.forEach(ship => {
        if (ship.usuario === selectedUser) {
          userCounts[ship.name] = (userCounts[ship.name] || 0) + 1;
        }
        // No contar las de otros usuarios cuando está en "Mi flota"
      });
    } else {
      // Si no hay usuario seleccionado (Toda la flota), separar por usuario actual vs otros
      rawShips.forEach(ship => {
        if (ship.usuario === localUsername) {
          userCounts[ship.name] = (userCounts[ship.name] || 0) + 1;
        } else {
          otherCounts[ship.name] = (otherCounts[ship.name] || 0) + 1;
        }
      });
    }
    
    return { user: userCounts, others: otherCounts };
  }, [rawShips, localUsername, selectedUser]);

  // Filtrar las naves del catálogo según los criterios seleccionados (siempre mostrar todas las naves)
  const filteredCatalogShips = useMemo(() => {
    if (!catalogShips) return [];
    
    return catalogShips.filter((ship: Ship) => {
      const matchesSearch = !searchQuery || 
        ship.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = !selectedType || 
        (ship.type && ship.type.toLowerCase() === selectedType.toLowerCase());
      const matchesSize = !selectedSize || 
        (ship.size && ship.size.toLowerCase() === selectedSize.toLowerCase());
      const matchesFocus = !selectedFocus || ship.focus === selectedFocus;
      const matchesProductionStatus = !selectedProductionStatus || 
        (ship.production_status && ship.production_status.toLowerCase() === selectedProductionStatus.toLowerCase());
      const matchesManufacturer = currentManufacturer === 'all' || 
        ship.manufacturer.code === currentManufacturer;

      return matchesSearch && matchesType && matchesSize &&
        matchesFocus && matchesProductionStatus && matchesManufacturer;
    });
  }, [catalogShips, searchQuery, selectedType, selectedSize, selectedFocus, 
      selectedProductionStatus, currentManufacturer]);

  // Ordenar las naves según el criterio seleccionado
  const orderedShips = useMemo(() => {
    let ships: (Ship | GroupedShip)[] = isGrouped ? groupShips(filteredShips, selectedUser) : [...filteredShips];

    // Ordenar las naves del usuario primero
    ships.sort((a, b) => {
      if (a.usuario === localUsername && b.usuario !== localUsername) return -1;
      if (a.usuario !== localUsername && b.usuario === localUsername) return 1;
      return 0;
    });

    // Aplicar el ordenamiento adicional según el campo seleccionado
    ships.sort((a, b) => {
      let comparison = 0;
      switch (sortConfig.field) {
        case 'name':
          comparison = sortConfig.direction === 'asc' ?
            a.name.localeCompare(b.name) :
            b.name.localeCompare(a.name);
          break;
        case 'size': {
          type ShipSize = 'vehicle' | 'v' | 'snub' | 'n' | 'small' | 's' | 'medium' | 'm' | 'large' | 'l' | 'capital' | 'c';
          const sizeOrder: Record<ShipSize, number> = {
            'vehicle': 1, 'v': 1,
            'snub': 2, 'n': 2,
            'small': 3, 's': 3,
            'medium': 4, 'm': 4,
            'large': 5, 'l': 5,
            'capital': 6, 'c': 6
          };
          const aSize = (a.size || '').toLowerCase() as ShipSize;
          const bSize = (b.size || '').toLowerCase() as ShipSize;
          const aValue = sizeOrder[aSize] || 0;
          const bValue = sizeOrder[bSize] || 0;
          comparison = sortConfig.direction === 'asc' ? 
            ((aValue - bValue) || a.name.localeCompare(b.name)) : 
            ((bValue - aValue) || a.name.localeCompare(b.name));
          break;
        }
        case 'cargo': {
          const aCargo = a.cargo_capacity || 0;
          const bCargo = b.cargo_capacity || 0;
          comparison = sortConfig.direction === 'asc' ?
            (aCargo - bCargo || a.name.localeCompare(b.name)) :
            (bCargo - aCargo || a.name.localeCompare(b.name));
          break;
        }
      }
      return comparison;
    });

    return ships;
  }, [filteredShips, isGrouped, sortConfig, localUsername, selectedUser]);

  // Aplicar modo de comparación
  const shipsWithCompare: (Ship | GroupedShip)[] = useMemo(() => {
    if (!isCompareMode) return orderedShips;

    if (shipsToCompare.length === 1) {
      const selectedShipName = shipsToCompare[0].name;
      const otherShips = orderedShips.filter(ship => ship.name !== selectedShipName);
      return [shipsToCompare[0], ...otherShips];
    }

    // Si hay 2 o más naves seleccionadas, filtrarlas de la lista
    const selectedShipNames = new Set(shipsToCompare.map(s => s.name));
    return orderedShips.filter(ship => !selectedShipNames.has(ship.name));
  }, [orderedShips, isCompareMode, shipsToCompare]);

  // Paginar las naves
  const displayedShips: (Ship | GroupedShip)[] = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return shipsWithCompare.slice(startIndex, endIndex);
  }, [shipsWithCompare, currentPage, itemsPerPage]);

  // Y actualizamos las referencias en los cálculos de contadores
  const shipsByManufacturer = React.useMemo(() => {
    if (!filteredShips) return {};
    return filteredShips.reduce((acc: ShipsByManufacturer, ship: Ship) => {
      const manufacturer = ship.manufacturer.code;
      if (!acc[manufacturer]) {
        acc[manufacturer] = {
          ships: [],
          name: ship.manufacturer.name
        };
      }
      acc[manufacturer].ships.push(ship);
      return acc;
        }, {});
  }, [filteredShips]);

  const shipsByType = React.useMemo(() => {
    if (!filteredShips) return {};
    return filteredShips.reduce((acc: ShipsByType, ship: Ship) => {
      const type = (ship.type || 'undefined').toLowerCase();
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(ship);
      return acc;
    }, {});
  }, [filteredShips]);

  const shipsBySize = React.useMemo(() => {
    if (!filteredShips) return {};
    return filteredShips.reduce((acc: ShipsBySize, ship: Ship) => {
      const size = ship.size?.toLowerCase() || 'undefined';
      if (!acc[size]) {
        acc[size] = [];
      }
      acc[size].push(ship);
      return acc;
    }, {});
  }, [filteredShips]);

  useEffect(() => {
    if (!isAdmin && !isCaptain) {
      setOrganizationUsers([]);
      return;
    }

    let cancelled = false;
    fleetService
      .getOrganizationUsers()
      .then((users) => {
        if (!cancelled) setOrganizationUsers(users);
      })
      .catch(() => {
        if (!cancelled) setOrganizationUsers([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin, isCaptain]);

  const usersList = useMemo(() => {
    if (!isAdmin && !isCaptain) {
      return localUsername ? [localUsername] : [];
    }

    const users = new Set<string>();
    organizationUsers.forEach((user) => {
      if (user?.trim()) users.add(user.trim());
    });
    rawShips?.forEach((ship) => {
      if (ship.usuario?.trim()) users.add(ship.usuario.trim());
    });

    return Array.from(users).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rawShips, localUsername, isAdmin, isCaptain, organizationUsers]);

  // Ocultar el menú de usuarios si no es admin o capitán
  const showUsersFilter = React.useMemo(() => {
    return isAdmin || isCaptain;
  }, [isAdmin, isCaptain]);

  // Resetear la página cuando cambian los filtros
  React.useEffect(() => {
    setCurrentPage(1);
  }, [currentManufacturer, selectedType, selectedSize, searchQuery, selectedUser]);

  // Asegurar que shipTypes nunca sea null
  const shipTypesArray = shipTypes || [];

  if (shipsLoading) return <div className="loading">Cargando flota...</div>;
  if (shipsError) return <div className="error">Error al cargar la flota: {shipsError.message}</div>;
  if (!rawShips) return null;

  return (
    <ProtectedRoute>
      <div className="fleet-page">
        <Header 
          onStartTutorial={startTutorial} 
          hasNeverCompletedTutorial={hasNeverCompletedTutorial()}
          onToggleMenu={toggleMenu}
          isMenuOpen={isMenuOpen}
        />

        <nav className="navigation-section" ref={menuRef}>
          <Navigation 
            onCompareToggle={handleCompareToggle}
            isCompareMode={isCompareMode}
            compareCount={shipsToCompare.length}
            showCompareButton={true}
            onExportFleetViewer={exportToFleetViewer}
            onExportFleetStarjump={exportToStarjump}
            showExportButton={true}
            exportCount={filteredShips.length}
            isMenuOpen={isMenuOpen}
            onCloseMenu={closeMenu}
          />
        </nav>

        <div className="search-section">
          <ViewModeButtons
            isGrouped={isGrouped}
            setIsGrouped={setIsGrouped}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            username={localUsername}
            viewType={viewType}
            setViewType={setViewType}
            catalogSortBy={catalogSortBy}
            setCatalogSortBy={setCatalogSortBy}
          />
          <SearchBar 
            onSearch={setSearchQuery}
            onClearFilters={handleClearFilters}
            minChars={3}
          />
        </div>

        {/* Filtros simplificados solo para móvil */}
        <div className="mobile-filters-section">
          <div className="mobile-filters-header">
            <button
              className="mobile-filters-toggle"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              {showMobileFilters ? 'Menos filtros' : 'Más filtros'}
            </button>
          </div>
          
          {showMobileFilters && (
            <div className="filters-row">
              <div className="filter-group">
                <label>Ordenación:</label>
                <select 
                  value={`${sortConfig.field}-${sortConfig.direction}`}
                  onChange={(e) => {
                    const [field, direction] = e.target.value.split('-');
                    setSortConfig({ 
                      field: field as 'name' | 'size' | 'cargo', 
                      direction: direction as 'asc' | 'desc'
                    });
                  }}
                  className="filter-select"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="size-asc">Tamaño (A-Z)</option>
                  <option value="size-desc">Tamaño (Z-A)</option>
                  <option value="cargo-asc">Capacidad de carga (A-Z)</option>
                  <option value="cargo-desc">Capacidad de carga (Z-A)</option>
                </select>
              </div>

              {showUsersFilter && (
                <div className="filter-group">
                  <label>Usuario:</label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Todos</option>
                    {usersList.map((user: string) => (
                      <option key={user} value={user}>
                        {user === 'anonymous' ? 'Anónimo' : user}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="filter-group">
                <label>Tipos:</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="filter-select"
                >
                  <option value="">Todos</option>
                  {shipTypesArray?.map(type => (
                    <option key={type} value={type}>
                      {TYPE_TRANSLATIONS[type?.toLowerCase()] || type || 'Sin tipo'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Tamaños:</label>
                <select
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  className="filter-select"
                >
                  <option value="">Todos</option>
                  <option value="vehicle">Vehículo</option>
                  <option value="small">Pequeño</option>
                  <option value="medium">Mediano</option>
                  <option value="large">Grande</option>
                  <option value="capital">Capital</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Enfoque:</label>
                <select
                  value={selectedFocus}
                  onChange={(e) => setSelectedFocus(e.target.value)}
                  className="filter-select"
                >
                  <option value="">Todos</option>
                  {Array.from(new Set(rawShips?.map(ship => ship.focus).filter(Boolean))).map(focus => (
                    <option key={focus} value={focus}>
                      {focus}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <main className="page-content">
          <aside className={`manufacturers-nav ${!showManufacturers ? 'collapsed' : ''}`}>
            <button
              className="collapse-button left"
              onClick={handleManufacturersToggle}
              title={showManufacturers ? "Ocultar fabricantes" : "Mostrar fabricantes"}
            />
            <div className="manufacturers-content">
              <button
                className={`manufacturer-button all-button ${currentManufacturer === 'all' ? 'active' : ''}`}
                onClick={() => setCurrentManufacturer('all')}
              >
                TODOS
              </button>
              {manufacturers?.sort((a, b) => a.name.localeCompare(b.name)).map(manufacturer => (
                <button
                  key={manufacturer.code}
                  className={`manufacturer-button ${currentManufacturer === manufacturer.code ? 'active' : ''}`}
                  onClick={() => setCurrentManufacturer(manufacturer.code)}
                  data-name={manufacturer.name}
                >
                  <img 
                    src={`/media/logos/${manufacturer.code}.png`} 
                    alt={manufacturer.name}
                    className="manufacturer-logo"
                  />
                  <span className="ship-count">
                    {shipsByManufacturer[manufacturer.code]?.ships.length || 0}
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <section className="ships-container">

            {viewType !== 'catalog' && (
              <>
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(shipsWithCompare.length / itemsPerPage)}
                  onPageChange={setCurrentPage}
                />
                {isCompareMode && shipsToCompare.length >= 2 && (
                  <div className="comparison-layout">
                    <ShipComparison ships={shipsToCompare} />
                    <div className="compared-ships-list">
                      {shipsToCompare.map(ship => (
                        <ShipCard
                          key={ship.name}
                          ship={ship}
                          onClick={() => {}}
                          duplicateCount={1}
                          username={localUsername}
                          isAnonymous={isAnonymous}
                          showAddButton={false}
                          showRemoveButton={false}
                          showUsers={false}
                          isGrouped={false}
                          showCount={false}
                          isCompareMode={true}
                          onCompareSelect={handleShipCompareSelect}
                          isSelectedForCompare={true}
                          isAdmin={isAdmin}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Mostrar mensaje de flota vacía si no hay naves y no se ha completado el tutorial */}
            {rawShips?.length === 0 && !isTutorialCompleted ? (
              <EmptyFleetMessage onStartTutorial={startTutorial} />
            ) : viewType === 'catalog' && filteredCatalogShips ? (
              <ShipCatalogView
                ships={filteredCatalogShips}
                fleetCounts={fleetCounts}
                sortBy={catalogSortBy}
                onShipClick={(ship) => {
                  setSelectedShip(ship);
                }}
              />
            ) : (
              <ShipList
                ships={displayedShips}
                pagination={{
                  currentPage,
                  totalPages: Math.ceil(orderedShips.length / itemsPerPage),
                  onPageChange: setCurrentPage
                }}
                onShipSelect={setSelectedShip}
                viewType={viewType === 'catalog' ? 'grid' : viewType}
                showAddButton={false}
                showRemoveButton={!isCompareMode && !isGrouped}
                username={localUsername}
                isAnonymous={isAnonymous}
                showUsers={true}
                isGrouped={isGrouped}
                showCount={isGrouped}
                onShipRemoved={() => {
                  window.location.reload();
                }}
                isCompareMode={isCompareMode}
                onCompareSelect={handleShipCompareSelect}
                selectedForCompare={shipsToCompare.map(s => s.name)}
                selectedType={selectedType}
                selectedSize={selectedSize}
                isAdmin={isAdmin}
                isUserFiltered={!!selectedUser}
              />
            )}
          </section>

          <FiltersMenu
            showFilters={showFilters}
            handleFiltersToggle={handleFiltersToggle}
            isGrouped={isGrouped}
            setIsGrouped={setIsGrouped}
            sortConfig={sortConfig}
            setSortConfig={setSortConfig}
            showUsersFilter={showUsersFilter}
            showUserMenu={showUserMenu}
            toggleUserMenu={toggleUserMenu}
            selectedUser={selectedUser}
            showProductionStatusMenu={showProductionStatusMenu}
            toggleProductionStatusMenu={toggleProductionStatusMenu}
            selectedProductionStatus={selectedProductionStatus}
            setSelectedProductionStatus={setSelectedProductionStatus}
            setSelectedUser={setSelectedUser}
            usersList={usersList}
            filteredShips={filteredShips}
            showTypeMenu={showTypeMenu}
            toggleTypeMenu={toggleTypeMenu}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            shipTypes={shipTypesArray}
            shipsByType={shipsByType}
            showSizeMenu={showSizeMenu}
            toggleSizeMenu={toggleSizeMenu}
            selectedSize={selectedSize}
            setSelectedSize={setSelectedSize}
            shipsBySize={shipsBySize}
            showFocusMenu={showFocusMenu}
            toggleFocusMenu={toggleFocusMenu}
            selectedFocus={selectedFocus}
            setSelectedFocus={setSelectedFocus}
            rawShips={rawShips}
            username={localUsername}
            isAdmin={isAdmin}
          />
        </main>


      </div>
    </ProtectedRoute>
  );
}; 